import { RouteProp } from '@react-navigation/native';
import React, { useEffect, useMemo } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useMedicalProfileFiles from '../../../../../hooks/api/useMedicalProfileFiles';
import { getIntl } from '../../../../../utils/intl';
import FileCard from '../../../../shared/FileCard';
import { useTempFilesPicker } from '../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import SearchField from './SearchField';
import SearchContent from './SearchContent';
import FolderCard from '../../../../shared/FolderCard';
import { useFolderActions } from './useFolderActions';
import HeaderContainer from '../../../../v2BaseComponents/HeaderContainer';
import { Action } from '../../../../v2BaseComponents/ActionButtonLayout/types';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import FileFolderPath from '../../../../shared/FileFolderPath';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import EmptyStateHint from '../../../../shared/EmptyStateHint';
import { useIsE2E } from '../../../../../hooks/useIsE2E';
import useRecordEvent from '../../../../../hooks/useRecordEvent';
import { useHasReachedFileUploadLimit } from '../../../../../hooks/api/useMedicalProfileUsageMetric';
import MedicalProfileUsageIndicator from '../../../../shared/MedicalProfileUsageIndicator';
import Button from '../../../../v2BaseComponents/Button';
import { PRScrollView } from '../../../../shared/Scroll';

const intl = getIntl('FilesScreen', {
    addFile: 'Add Files',
    noSearchResults: 'No search results for "{searchText}"...',
    addSuggestedFolders: 'Add Suggested Folders',
});

export type FilesScreenOwnProps = {};

export type FilesScreenProps = FilesScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'files'>;
};

const FilesScreen: React.FC<FilesScreenProps> = (props) => {
    const hasReachedFileUploadLimit = useHasReachedFileUploadLimit(props.route.params.profileId);
    const [focusedFolderId, setFocusedFolderId] = React.useState<string>(props.route.params.defaultFolderId || 'root');
    useEffect(() => {
        if (props.route.params.defaultFolderId) {
            setFocusedFolderId(props.route.params.defaultFolderId);
        }
    }, [props.route.params.defaultFolderId]);
    const isE2E = useIsE2E();
    const [search, setSearch] = React.useState('');
    const hasFilesEdit = useHasPermission(props.route.params.profileId, 'files', 'edit');
    const { isLoadingProfileFiles, profileFiles, createFile, isCreatingFile, folderMetadata, isLoadingFolderMetadata } =
        useMedicalProfileFiles({
            medicalProfileId: props.route.params.profileId,
            search,
            folderId: focusedFolderId,
        });
    const rootFolderProfileFiles = useMedicalProfileFiles({
        medicalProfileId: props.route.params.profileId,
        search,
        folderId: 'root',
    });
    const { navigate } = useProfilesTabNavigation();
    const sortedFiles = useMemo(
        () => profileFiles.sort((a, b) => new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()),
        [profileFiles],
    );
    const folderActions = useFolderActions({
        medicalProfileId: props.route.params.profileId,
        folderId: focusedFolderId,
        onSetFolderId: setFocusedFolderId,
        folderOrganizationId:
            folderMetadata?.targetFolder?.isRoot === false
                ? folderMetadata?.targetFolder?.folder?.organizationId || null
                : null,
    });
    const addFileTempPicker = useTempFilesPicker({
        onPick: (files) => {
            createFile({
                tempFiles: files.map((file) => ({
                    tempFileId: file.tempFileId,
                    fileName: file.originalName || file.name,
                    contentType: file.contentType,
                })),
                medicalProfileFileFolderId: focusedFolderId,
            });
            record('addFile');
        },
    });
    const record = useRecordEvent();
    const allActions: Action[] = [
        {
            title: intl('addFile'),
            icon: 'PlusCircle',
            onPress: addFileTempPicker.onPress,
            show: hasFilesEdit,
            isDisabled: isCreatingFile || addFileTempPicker.isLoadingFile || hasReachedFileUploadLimit,
        },
        ...folderActions.actions,
    ];

    // Don't show the Notes Files folder.  These are hidden from lists except e2e tests since it is needed for various validations
    const displayedSubFolders = (folderMetadata?.descendants.medicalProfileFileFolders || []).filter(
        (_) => isE2E || !_.systemFolderType,
    );

    const hasAnyFiles = useMemo(() => {
        let total = rootFolderProfileFiles.profileFiles.length;
        rootFolderProfileFiles.folderMetadata?.descendants.medicalProfileFileFolders
            .filter((_) => isE2E || !_.systemFolderType)
            .forEach((folder) => {
                if (folder?.descendantFilesCount) {
                    total += folder.descendantFilesCount;
                }
            });

        return total > 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        rootFolderProfileFiles.profileFiles.length,
        rootFolderProfileFiles.folderMetadata?.descendants.medicalProfileFileFolders,
    ]);

    const hasAnyFolders = displayedSubFolders.length > 0;
    const hasAnyFilesOrFolders = hasAnyFiles || hasAnyFolders;

    const renderContent = () => {
        if (!search) {
            return (
                <AnimateLoadedContent
                    isLoading={
                        isLoadingFolderMetadata ||
                        isLoadingProfileFiles ||
                        rootFolderProfileFiles.isLoadingProfileFiles ||
                        rootFolderProfileFiles.isLoadingFolderMetadata
                    }
                    style={{
                        flex: 1,
                    }}
                >
                    {() => (
                        <>
                            <Stack vertical fullWidth flex={1}>
                                <HeaderContainer>
                                    <Stack horizontal fullWidth justify="space-between" align="center">
                                        <PRScrollView
                                            horizontal
                                            style={{ flex: 1 }}
                                            showsHorizontalScrollIndicator={false}
                                        >
                                            <FileFolderPath
                                                folderId={focusedFolderId}
                                                medicalProfileId={props.route.params.profileId}
                                                onFolderSelected={setFocusedFolderId}
                                                mobileMaxPathLength={30}
                                            />
                                        </PRScrollView>
                                        <MedicalProfileUsageIndicator
                                            medicalProfileId={props.route.params.profileId}
                                            metricType="file-upload"
                                        />
                                    </Stack>
                                </HeaderContainer>
                                <ActionButtonLayout actions={allActions} scrollable>
                                    <MainContentContainer>
                                        <ContentStack>
                                            {displayedSubFolders
                                                .sort((a, b) => a.folderName.localeCompare(b.folderName))
                                                .map((folder) => (
                                                    <FolderCard
                                                        key={folder.medicalProfileFileFolderId}
                                                        folder={folder}
                                                        onPress={() => {
                                                            setFocusedFolderId(folder.medicalProfileFileFolderId);
                                                        }}
                                                    />
                                                ))}
                                            {!hasAnyFilesOrFolders && folderMetadata?.targetFolder?.isRoot === true ? (
                                                <EmptyStateHint
                                                    variant="files-or-folders"
                                                    renderSubContent={() => (
                                                        <Stack horizontal fullWidth justify="center">
                                                            <Button
                                                                icon="Plus"
                                                                title={intl('addSuggestedFolders')}
                                                                onPress={() => {
                                                                    navigate('add-suggested-profile-folders', {
                                                                        profileId: props.route.params.profileId,
                                                                    });
                                                                }}
                                                                variant="secondary"
                                                                testID="add-suggested-folders-button"
                                                            />
                                                        </Stack>
                                                    )}
                                                />
                                            ) : null}
                                            {!hasAnyFiles && hasAnyFilesOrFolders ? (
                                                <EmptyStateHint variant="files" />
                                            ) : null}
                                            {sortedFiles.map((file) => (
                                                <FileCard
                                                    key={file.medicalProfileFileId}
                                                    file={file}
                                                    onPress={() => {
                                                        navigate('file', {
                                                            profileId: props.route.params.profileId,
                                                            fileId: file.medicalProfileFileId,
                                                        });
                                                    }}
                                                />
                                            ))}
                                        </ContentStack>
                                    </MainContentContainer>
                                </ActionButtonLayout>
                                {addFileTempPicker.E2EInput}
                            </Stack>
                            {folderActions.Prompts}
                        </>
                    )}
                </AnimateLoadedContent>
            );
        } else {
            return (
                <SearchContent
                    search={search}
                    medicalProfileId={props.route.params.profileId}
                    onFocusFolder={(folderId: string) => {
                        setFocusedFolderId(folderId);
                        setSearch('');
                    }}
                />
            );
        }
    };

    return (
        <>
            <ProfileHeaderContainer profileId={props.route.params.profileId}>
                <HeaderContainer>
                    <SearchField value={search} onChange={setSearch} />
                </HeaderContainer>
                {renderContent()}
            </ProfileHeaderContainer>
        </>
    );
};

export default React.memo(FilesScreen);
